import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  MapPin,
  PackageCheck,
  Search,
  ArrowUpRight,
  MoreHorizontal,
  AlertOctagon,
  AlertTriangle,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { dispatchService } from '@/services/dispatch';
import type { DispatchOrder, DispatchStatus } from '@/types/dispatch';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { DispatchDetailSheet } from './DispatchDetailSheet';

// Synthetic filters, not DispatchStatus values:
//   AWB_FAILED — PENDING dispatches whose carrier AWB booking failed
//                (failureReason set); they'd otherwise hide in Pending.
//   MOVING     — the in-transit worklist (PICKED/IN_TRANSIT/OUT_FOR_DELIVERY).
//   LATE       — a moving shipment past its expected-delivery date.
type StatusFilter = 'all' | 'MOVING' | 'LATE' | DispatchStatus | 'AWB_FAILED';

/** A dispatch whose carrier AWB booking failed and has no tracking yet. */
function isAwbFailed(d: DispatchOrder): boolean {
  return d.status === 'PENDING' && !!d.failureReason && !d.trackingNumber;
}

/** Statuses where the parcel is physically in the carrier network. */
const MOVING_STATUSES: DispatchStatus[] = ['PICKED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'];

function isMoving(d: DispatchOrder): boolean {
  return MOVING_STATUSES.includes(d.status);
}

/** A moving shipment that has blown past its expected-delivery date. */
function isLate(d: DispatchOrder): boolean {
  return isMoving(d) && !!d.expectedDeliveryAt && new Date(d.expectedDeliveryAt) < new Date();
}

const STATUS_LABEL: Record<DispatchStatus, string> = {
  PENDING: 'Pending',
  READY_FOR_PICKUP: 'Packed',
  PICKED: 'Picked',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for del',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
  RETURN_INITIATED: 'Return init',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<DispatchStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  PENDING: 'muted',
  READY_FOR_PICKUP: 'warning',
  PICKED: 'warning',
  IN_TRANSIT: 'success',
  OUT_FOR_DELIVERY: 'success',
  DELIVERED: 'success',
  FAILED: 'destructive',
  RETURN_INITIATED: 'warning',
  RETURNED: 'muted',
  CANCELLED: 'muted',
};

export function DispatchPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<DispatchOrder | null>(null);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dispatch', { q: debounced }],
    queryFn: () => dispatchService.list({ search: debounced || undefined, pageSize: 200 }),
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  // Deep link — /dispatch?id=<dispatchId> opens that dispatch's detail.
  // Used by the "Open dispatch" link on the sales-order detail.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && !selected && allRows.length > 0) {
      const match = allRows.find((d) => d.id === id);
      if (match) setSelected(match);
    }
  }, [searchParams, allRows, selected]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      MOVING: 0,
      LATE: 0,
      PENDING: 0,
      READY_FOR_PICKUP: 0,
      PICKED: 0,
      IN_TRANSIT: 0,
      OUT_FOR_DELIVERY: 0,
      DELIVERED: 0,
      FAILED: 0,
      RETURN_INITIATED: 0,
      RETURNED: 0,
      CANCELLED: 0,
      AWB_FAILED: 0,
    };
    for (const i of allRows) {
      c[i.status] += 1;
      if (isAwbFailed(i)) c.AWB_FAILED += 1;
      if (isMoving(i)) c.MOVING += 1;
      if (isLate(i)) c.LATE += 1;
    }
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    if (filter === 'AWB_FAILED') return allRows.filter(isAwbFailed);
    if (filter === 'MOVING') return allRows.filter(isMoving);
    if (filter === 'LATE') return allRows.filter(isLate);
    return allRows.filter((i) => i.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(
    () => ({
      ready: counts.READY_FOR_PICKUP + counts.PICKED,
      inTransit: counts.IN_TRANSIT + counts.OUT_FOR_DELIVERY,
      delivered: counts.DELIVERED,
      late: counts.LATE,
    }),
    [counts],
  );

  const tabs: StatusTabOption[] = [
    { value: 'all',              label: 'All',         count: counts.all },
    { value: 'MOVING',           label: 'Moving',      count: counts.MOVING },
    { value: 'LATE',             label: 'Late',        count: counts.LATE },
    { value: 'PENDING',          label: 'Pending',     count: counts.PENDING },
    { value: 'READY_FOR_PICKUP', label: 'Packed',      count: counts.READY_FOR_PICKUP },
    { value: 'PICKED',           label: 'Picked',      count: counts.PICKED },
    { value: 'IN_TRANSIT',       label: 'In transit',  count: counts.IN_TRANSIT },
    { value: 'OUT_FOR_DELIVERY', label: 'Out for del', count: counts.OUT_FOR_DELIVERY },
    { value: 'DELIVERED',        label: 'Delivered',   count: counts.DELIVERED },
    { value: 'FAILED',           label: 'Failed',      count: counts.FAILED },
    { value: 'CANCELLED',        label: 'Cancelled',   count: counts.CANCELLED },
    { value: 'AWB_FAILED',       label: 'Booking failed', count: counts.AWB_FAILED },
  ];

  const columns: Column<DispatchOrder>[] = [
    {
      key: 'no',
      header: 'Dispatch #',
      width: '180px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[12px] font-medium text-foreground">{r.dispatchNumber}</span>
            {r.direction === 'REVERSE' && (
              <Badge variant="info" className="h-4 text-[9.5px]" title="Reverse pickup — customer to warehouse">
                Return
              </Badge>
            )}
          </div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.salesOrder.orderNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-[12.5px] text-foreground">{r.salesOrder.customer.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.salesOrder.customer.code}
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'partner',
      header: 'Partner',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-[12.5px] text-foreground">{r.partner?.name ?? '—'}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.partner?.code ?? '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'tracking',
      header: 'Tracking #',
      width: '180px',
      cell: (r) =>
        r.trackingNumber ? (
          <span className="font-mono text-[11.5px]">{r.trackingNumber}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'destination',
      header: 'Destination',
      width: '160px',
      cell: (r) => (
        <div className="text-[12.5px] text-muted-foreground">
          {r.shippingCity ?? '—'}
          {r.shippingState ? `, ${r.shippingState}` : ''}
        </div>
      ),
    },
    {
      key: 'eta',
      header: 'Expected delivery',
      width: '190px',
      cell: (r) => {
        if (!r.expectedDeliveryAt) {
          return <span className="text-[11.5px] text-muted-foreground">—</span>;
        }
        const late = isLate(r);
        return (
          <div className="space-y-0.5">
            <div
              className={
                late
                  ? 'font-mono text-[11.5px] tabular-nums font-medium text-destructive'
                  : 'font-mono text-[11.5px] tabular-nums text-muted-foreground'
              }
            >
              {fmt.dateTime(r.expectedDeliveryAt)}
            </div>
            <div
              className={
                late ? 'text-[10.5px] font-medium text-destructive' : 'text-[10.5px] text-muted-foreground'
              }
            >
              {late ? `Late · ${fmt.relative(r.expectedDeliveryAt)}` : fmt.relative(r.expectedDeliveryAt)}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '210px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
              {STATUS_LABEL[r.status]}
            </Badge>
            {isAwbFailed(r) && (
              <Badge
                variant="destructive"
                className="gap-1 text-[9.5px]"
                title={r.failureReason ?? 'Carrier AWB booking failed'}
              >
                <AlertOctagon className="h-3 w-3" />
                Booking failed
              </Badge>
            )}
          </div>
          {(r.status === 'FAILED' || r.status === 'CANCELLED') && r.failureReason && (
            <div
              className="max-w-[200px] truncate text-[10.5px] text-muted-foreground"
              title={r.failureReason}
            >
              {r.status === 'CANCELLED' ? 'Cancelled: ' : 'Reason: '}
              {r.failureReason}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        {!embedded && <ListPageHeader title="Dispatch" count={counts.all} />}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard label="Ready / picked" value={fmt.number(summary.ready)} tone="primary" icon={Truck} />
          <KpiCard label="In transit" value={fmt.number(summary.inTransit)} tone="info" icon={MapPin} />
          <KpiCard label="Delivered" value={fmt.number(summary.delivered)} tone="success" icon={PackageCheck} />
          <KpiCard
            label="Late"
            value={fmt.number(summary.late)}
            tone={summary.late > 0 ? 'destructive' : 'default'}
            icon={AlertTriangle}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dispatch # …"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No dispatches yet'
                : filter === 'MOVING'
                  ? 'Nothing in transit right now'
                  : filter === 'LATE'
                    ? 'No late shipments'
                    : filter === 'AWB_FAILED'
                      ? 'No failed AWB bookings'
                      : `No ${STATUS_LABEL[filter as DispatchStatus].toLowerCase()} dispatches`
            }
            emptyDescription={
              filter === 'all'
                ? 'Dispatches are created from a sales order once it is READY_FOR_DISPATCH.'
                : filter === 'MOVING'
                  ? 'Shipments appear here once their dispatch is PICKED, IN_TRANSIT, or OUT_FOR_DELIVERY.'
                  : filter === 'LATE'
                    ? 'A moving shipment shows here when it passes its expected-delivery date.'
                    : 'Try a different status filter.'
            }
          />
        )}

        <DispatchDetailSheet
          dispatch={selected}
          open={!!selected}
          onOpenChange={(o) => {
            if (!o) {
              setSelected(null);
              // Drop the deep-link param so closing doesn't re-open it.
              if (searchParams.get('id')) {
                searchParams.delete('id');
                setSearchParams(searchParams, { replace: true });
              }
            }
          }}
        />
      </div>
    </TooltipProvider>
  );
}
